/**
 * Run the residential fee day calculator across a billing period.
 *
 * Days are sorted. A duplicate date throws. Each day's close becomes the next
 * day's open, including a held day: the hold stops posting, not the statutory
 * counters. C3 must not post a held day and must not treat that close as a
 * posted balance without its own rule.
 *
 * Fee advice is the latest record whose key is on or before the day. No record
 * means empty means, which the day calculator treats as pending. Missing
 * accommodation, fee reduction, and HELF are zero. A missing bed-reservation
 * entry is "no agreement".
 *
 * The fee-reduction remainder is not carried. Each day receives only the
 * amount in `feeReductionByDate` for that date.
 *
 * Rates come from `rateResolver`. A null row holds that day with
 * `RATE_PERIOD_MISSING`. This runner does not look up a rate table.
 */

import { assertResidentialFeeDate, type ResidentialFeeRateResolver } from './rates';
import {
  computeResidentialFeeDay,
  type ResidentialAccommodationContributionInput,
  type ResidentialBedReservationInput,
  type ResidentialFeeCapOverrides,
  type ResidentialFeeDayResult,
  type ResidentialFeeMeansInput,
  type ResidentialFeePositions,
} from './day-calculator';
import type { ResidentialFeeCohort } from './cohorts';
import type { ResidentialDayState } from './day-states';
import type { ResidentialFeePolicyV3 } from './policy';

export interface ResidentialFeePeriodDay {
  date: string;
  dayState: ResidentialDayState;
  socialLeaveOrdinalFy?: number | null;
  hospitalEpisodeDay?: number | null;
  indexationDay?: boolean;
}

export interface ResidentialFeePeriodInput {
  days: readonly ResidentialFeePeriodDay[];
  cohort: ResidentialFeeCohort;
  positionsAtOpen?: Partial<ResidentialFeePositions> | null;
  /** Keys are tenant-zone ISO dates (effective from). The latest key on or before the day wins. */
  advicesByDate?: Readonly<Record<string, ResidentialFeeMeansInput>>;
  accommodationByDate?: Readonly<Record<string, number>>;
  /** D7. Exact-date keys. The class and advised maximum from the advice in force that day. */
  accommodationContributionByDate?: Readonly<
    Record<string, ResidentialAccommodationContributionInput>
  >;
  bedReservationByDate?: Readonly<Record<string, ResidentialBedReservationInput>>;
  feeReductionByDate?: Readonly<Record<string, number>>;
  helfAgreementIdsByDate?: Readonly<Record<string, readonly string[]>>;
  helfByDate?: Readonly<Record<string, number>>;
  policy?: Partial<ResidentialFeePolicyV3> | null;
  caps?: ResidentialFeeCapOverrides | null;
  /** Rate row for a billed date. Null holds that day. */
  rateResolver: ResidentialFeeRateResolver;
}

export interface ResidentialFeePeriodTotals {
  bdfNetCents: number;
  hotellingNetCents: number;
  ncccNetCents: number;
  mtcfNetCents: number;
  itfNetCents: number;
  accommodationNetCents: number;
  bedReservationNetCents: number;
  helfNetCents: number;
  careNetCents: number;
  grossDailyTotalCents: number;
  feeReductionAppliedCents: number;
  feeReductionUnappliedCents: number;
}

export interface ResidentialFeePeriodResult {
  days: ResidentialFeeDayResult[];
  positionsAtClose: ResidentialFeePositions;
  totals: ResidentialFeePeriodTotals;
}

export function computeResidentialFeePeriod(
  input: ResidentialFeePeriodInput,
): ResidentialFeePeriodResult {
  assertDateKeys(input.advicesByDate);
  assertDateKeys(input.accommodationByDate);
  assertDateKeys(input.accommodationContributionByDate);
  assertDateKeys(input.bedReservationByDate);
  assertDateKeys(input.feeReductionByDate);
  assertDateKeys(input.helfByDate);

  const days = [...input.days].sort((left, right) =>
    left.date < right.date ? -1 : left.date > right.date ? 1 : 0,
  );
  for (let index = 1; index < days.length; index += 1) {
    if (days[index]!.date === days[index - 1]!.date) {
      throw new RangeError(`Duplicate residential fee date: ${days[index]!.date}`);
    }
  }

  let positionsBefore = openingPositions(input.positionsAtOpen);
  const results: ResidentialFeeDayResult[] = [];
  for (const day of days) {
    const result = computeResidentialFeeDay({
      date: day.date,
      cohort: input.cohort,
      dayState: day.dayState,
      socialLeaveOrdinalFy: day.socialLeaveOrdinalFy,
      hospitalEpisodeDay: day.hospitalEpisodeDay,
      indexationDay: day.indexationDay,
      means: adviceOnDate(input.advicesByDate, day.date),
      positionsBefore,
      accommodationCents: centsOn(input.accommodationByDate, day.date),
      accommodationContribution: input.accommodationContributionByDate?.[day.date] ?? null,
      feeReductionCents: centsOn(input.feeReductionByDate, day.date),
      helfCents: centsOn(input.helfByDate, day.date),
      helfAgreementIds: input.helfAgreementIdsByDate?.[day.date],
      helfGstTreatment: input.helfAgreementIdsByDate?.[day.date]?.length
        ? 'GST_FREE_RESIDENTIAL_CARE'
        : 'TAX_REVIEW_REQUIRED',
      bedReservation: input.bedReservationByDate?.[day.date] ?? null,
      policy: input.policy,
      caps: input.caps,
      rates: input.rateResolver(day.date),
    });
    results.push(result);
    positionsBefore = result.positionsClose;
  }

  return {
    days: results,
    positionsAtClose:
      results.length === 0 ? positionsBefore : results[results.length - 1]!.positionsClose,
    totals: sumPeriod(results),
  };
}

function adviceOnDate(
  advices: Readonly<Record<string, ResidentialFeeMeansInput>> | undefined,
  date: string,
): ResidentialFeeMeansInput | null {
  if (advices === undefined) return null;
  let selectedKey: string | null = null;
  for (const key of Object.keys(advices)) {
    if (key <= date && (selectedKey === null || key > selectedKey)) selectedKey = key;
  }
  if (selectedKey === null) return null;
  return advices[selectedKey] ?? null;
}

function centsOn(map: Readonly<Record<string, number>> | undefined, date: string): number {
  const value = map?.[date];
  return value === undefined ? 0 : value;
}

function assertDateKeys(map: Readonly<Record<string, unknown>> | undefined): void {
  if (map === undefined) return;
  for (const key of Object.keys(map)) assertResidentialFeeDate(key);
}

function openingPositions(
  input?: Partial<ResidentialFeePositions> | null,
): ResidentialFeePositions {
  const positions: ResidentialFeePositions = {
    sharedLifetimeTotalCents: input?.sharedLifetimeTotalCents ?? 0,
    mtcfAnnualTotalCents: input?.mtcfAnnualTotalCents ?? 0,
    mtcfLifetimeTotalCents: input?.mtcfLifetimeTotalCents ?? 0,
    ncccDays: input?.ncccDays ?? 0,
    mtcfAnnualPeriodStart: input?.mtcfAnnualPeriodStart ?? null,
    sharedLifetimeCapExceededOn: input?.sharedLifetimeCapExceededOn ?? null,
  };
  nonNegativeCents('sharedLifetimeTotalCents', positions.sharedLifetimeTotalCents);
  nonNegativeCents('mtcfAnnualTotalCents', positions.mtcfAnnualTotalCents);
  nonNegativeCents('mtcfLifetimeTotalCents', positions.mtcfLifetimeTotalCents);
  nonNegativeCount('ncccDays', positions.ncccDays);
  if (positions.mtcfAnnualPeriodStart !== null) {
    assertResidentialFeeDate(positions.mtcfAnnualPeriodStart);
  }
  if (positions.sharedLifetimeCapExceededOn !== null) {
    assertResidentialFeeDate(positions.sharedLifetimeCapExceededOn);
  }
  return positions;
}

function nonNegativeCents(name: string, value: number): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative safe integer number of cents`);
  }
}

function nonNegativeCount(name: string, value: number): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative integer`);
  }
}

function sumPeriod(days: readonly ResidentialFeeDayResult[]): ResidentialFeePeriodTotals {
  const totals: ResidentialFeePeriodTotals = {
    bdfNetCents: 0,
    hotellingNetCents: 0,
    ncccNetCents: 0,
    mtcfNetCents: 0,
    itfNetCents: 0,
    accommodationNetCents: 0,
    bedReservationNetCents: 0,
    helfNetCents: 0,
    careNetCents: 0,
    grossDailyTotalCents: 0,
    feeReductionAppliedCents: 0,
    feeReductionUnappliedCents: 0,
  };
  for (const day of days) {
    totals.bdfNetCents += day.bdf.netCents;
    totals.hotellingNetCents += day.hotelling.netCents;
    totals.ncccNetCents += day.nccc.netCents;
    totals.mtcfNetCents += day.mtcf.netCents;
    totals.itfNetCents += day.itf.netCents;
    totals.accommodationNetCents += day.accommodation.netCents;
    totals.bedReservationNetCents += day.bedReservation.netCents;
    totals.helfNetCents += day.helf.netCents;
    totals.careNetCents += day.careNetCents;
    totals.grossDailyTotalCents += day.grossDailyTotalCents;
    totals.feeReductionAppliedCents += day.feeReductionAppliedCents;
    totals.feeReductionUnappliedCents += day.feeReductionUnappliedCents;
  }
  return totals;
}
